import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import type { User } from 'firebase/auth';
import { useAuth } from '../contexts/AuthContext';
import { useApiRoot } from '../contexts/ApiRootContext';

function formatPermission(value: string): string {
  return value
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function accountDisplayName(user: User): string {
  const name = user.displayName?.trim();
  if (name) return name;
  if (user.email) return user.email.split('@')[0] ?? user.email;
  return 'User';
}

function accountInitials(displayName: string, email: string | null): string {
  const parts = displayName.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    const a = parts[0][0];
    const b = parts[parts.length - 1][0];
    if (a && b) return (a + b).toUpperCase();
  }
  if (parts.length === 1 && parts[0].length >= 2) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  const local = email?.split('@')[0] ?? displayName;
  return local.slice(0, 2).toUpperCase() || '?';
}

export default function UserAccountMenu() {
  const { user, permissions, signOut } = useAuth();
  const { hasLink } = useApiRoot();
  const [open, setOpen] = useState(false);

  if (!user) return null;

  const fullName = accountDisplayName(user);
  const email = user.email;
  const initials = accountInitials(fullName, email);
  const showMyAra = hasLink('my');

  return (
    <div
      className="relative ml-2 shrink-0"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="true"
        className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-700 text-xs font-semibold text-white shadow-sm ring-2 ring-white transition hover:bg-emerald-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2"
      >
        {initials}
      </button>

      {open && (
        <div className="absolute right-0 top-full z-50 w-64 pt-1">
          <div
            role="menu"
            className="rounded-xl border border-gray-200 bg-white py-2 shadow-lg ring-1 ring-black/5"
          >
            <div className="border-b border-gray-100 px-4 py-3">
              <p className="text-sm font-semibold text-gray-900 break-words">{fullName}</p>
              {email && <p className="mt-1 text-xs text-gray-500 break-all">{email}</p>}
              {!email && <p className="mt-1 text-xs text-gray-400">No email on file</p>}
              {permissions.length > 0 && (
                <ul className="mt-2 space-y-0.5 border-t border-gray-100 pt-2 text-xs font-medium text-gray-600">
                  {permissions.map((permission) => (
                    <li key={permission} className="flex items-start gap-1">
                      <ShieldCheck className="mt-0.5 h-3 w-3 shrink-0" />
                      <span className="break-words">{formatPermission(permission)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="px-2 pt-1">
              {showMyAra && (
                <Link
                  to="/my-ara"
                  role="menuitem"
                  className="block w-full rounded-lg px-3 py-2 text-left text-sm font-medium text-gray-700 transition hover:bg-gray-50"
                  onClick={() => setOpen(false)}
                >
                  My ARA
                </Link>
              )}
              <button
                type="button"
                role="menuitem"
                className="w-full rounded-lg px-3 py-2 text-left text-sm font-medium text-red-600 transition hover:bg-red-50"
                onClick={() => {
                  setOpen(false);
                  void signOut();
                }}
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
